import { Check, ChevronRight, CirclePause, ImagePlus, Pencil, X } from "lucide-react";
import { forwardRef, useEffect, useId, useRef, useState, type HTMLAttributes } from "react";
import type { ItemResearchClaim, ResearchSellerDecision } from "../../domain/types";
import { cn } from "../../lib/cn";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

type DecisionClaim = Pick<
  ItemResearchClaim,
  "confidence" | "kind" | "rationale" | "sellerDecision" | "statement"
>;

interface ResearchDecisionCardProps extends Omit<HTMLAttributes<HTMLElement>, "onChange"> {
  busy?: boolean;
  basis?: string;
  claim: DecisionClaim;
  disabled?: boolean;
  evidenceSummary?: string;
  gatherEvidenceDisabled?: boolean;
  gatherEvidenceLabel?: string;
  onGatherEvidence?: () => void;
  onReviewEvidence?: () => void;
  recommendation: "gather" | "provisional" | "use";
  onDecision: (decision: ResearchSellerDecision) => void;
}

const useLabels: Record<ItemResearchClaim["kind"], string> = {
  identity: "Select identification",
  maker: "Select maker",
  date: "Select date",
  origin: "Select origin",
  material: "Select material",
  authenticity: "Select assessment",
  condition: "Select condition",
  history: "Select history",
  valuation: "Select valuation"
};

const provisionalLabels: Record<ItemResearchClaim["kind"], string> = {
  identity: "Use provisionally",
  maker: "Use provisionally",
  date: "Use provisionally",
  origin: "Use provisionally",
  material: "Use provisionally",
  authenticity: "Use provisionally",
  condition: "Use provisionally",
  history: "Use provisionally",
  valuation: "Use provisionally"
};

const rejectLabels: Record<ItemResearchClaim["kind"], string> = {
  identity: "Rule out candidate",
  maker: "Rule out maker",
  date: "Rule out date",
  origin: "Rule out origin",
  material: "Rule out material",
  authenticity: "Reject assessment",
  condition: "Mark inaccurate",
  history: "Mark unsupported",
  valuation: "Reject valuation"
};

const confidenceLabels: Record<ItemResearchClaim["confidence"], string> = {
  low: "Low confidence",
  medium: "Medium confidence",
  high: "High confidence"
};

export const ResearchDecisionCard = forwardRef<HTMLElement, ResearchDecisionCardProps>(function ResearchDecisionCard({
  basis,
  busy = false,
  claim,
  className,
  disabled = false,
  evidenceSummary,
  gatherEvidenceDisabled = false,
  gatherEvidenceLabel = "Add evidence",
  onGatherEvidence,
  onReviewEvidence,
  recommendation,
  onDecision,
  tabIndex = -1,
  ...props
}, ref) {
  const decision = claim.sellerDecision;
  const isPending = decision === "pending";
  const unresolvedLabel = claim.kind === "identity" ? "Save as unidentified" : "Leave unresolved";
  const decisionHeading = decision === "accepted"
    ? claim.confidence === "high"
      ? "Finding selected"
      : "Provisional finding selected"
    : decision === "rejected"
      ? "Finding ruled out"
        : decision === "needs_evidence"
        ? claim.kind === "identity" ? "Saved as unidentified" : "Kept unresolved"
        : recommendation === "use"
          ? "Supported identification"
          : recommendation === "provisional"
            ? "Possible identification"
            : "Possible match";
  const stateLabel = decision === "accepted"
    ? claim.confidence === "high"
      ? "Identification selected"
      : "Provisional identification selected"
    : decision === "rejected"
      ? "Identification ruled out"
      : decision === "needs_evidence"
        ? claim.kind === "identity" ? "Saved as unidentified" : "Kept unresolved"
        : undefined;
  const confidenceLabel = isPending && recommendation === "gather"
    ? "Needs verification"
    : confidenceLabels[claim.confidence];

  return (
    <article
      ref={ref}
      aria-busy={busy || undefined}
      className={cn(
        "research-decision-card",
        `research-decision-card-${decision}`,
        decision === "accepted" && claim.confidence !== "high" && "research-decision-card-provisional",
        className
      )}
      tabIndex={tabIndex}
      {...props}
    >
      <header className="research-decision-card-header">
        <strong>{decisionHeading}</strong>
        <Badge tone={claim.confidence === "high" ? "info" : claim.confidence === "medium" ? "warning" : "neutral"}>
          {confidenceLabel}
        </Badge>
      </header>
      <div className="research-decision-card-copy">
        <h4>{claim.statement}</h4>
        <ResearchDecisionReasoning basis={basis} rationale={claim.rationale} />
        {evidenceSummary ? (
          onReviewEvidence ? (
            <Button className="research-decision-evidence-action" variant="ghost" size="compact" type="button" onClick={onReviewEvidence}>
              Review {evidenceSummary} <ChevronRight size={15} />
            </Button>
          ) : (
            <span className="research-decision-evidence-summary">{evidenceSummary}</span>
          )
        ) : null}
      </div>
      <footer className="research-decision-card-footer">
        {isPending ? (
          <div role="group" aria-label={`Decide finding: ${claim.statement}`}>
            {recommendation === "gather" && onGatherEvidence ? (
              <>
                <Button
                  variant="primary"
                  type="button"
                  onClick={onGatherEvidence}
                  disabled={busy || disabled || gatherEvidenceDisabled}
                >
                  <ImagePlus size={16} /> {gatherEvidenceLabel}
                </Button>
                <Button variant="secondary" type="button" onClick={() => onDecision("needs_evidence")} disabled={busy || disabled}>
                  <CirclePause size={16} /> {unresolvedLabel}
                </Button>
              </>
            ) : recommendation === "use" ? (
              <>
                <Button variant="primary" type="button" onClick={() => onDecision("accepted")} disabled={busy || disabled}>
                  <Check size={16} /> {useLabels[claim.kind]}
                </Button>
                <Button variant="secondary" type="button" onClick={() => onDecision("needs_evidence")} disabled={busy || disabled}>
                  <CirclePause size={16} /> {unresolvedLabel}
                </Button>
              </>
            ) : recommendation === "provisional" ? (
              <>
                <Button variant="primary" type="button" onClick={() => onDecision("accepted")} disabled={busy || disabled}>
                  <Check size={16} /> {provisionalLabels[claim.kind]}
                </Button>
                <Button variant="secondary" type="button" onClick={() => onDecision("needs_evidence")} disabled={busy || disabled}>
                  <CirclePause size={16} /> {unresolvedLabel}
                </Button>
              </>
            ) : (
              <Button variant="primary" type="button" onClick={() => onDecision("needs_evidence")} disabled={busy || disabled}>
                <CirclePause size={16} /> {unresolvedLabel}
              </Button>
            )}
            <Button variant="ghost" type="button" onClick={() => onDecision("rejected")} disabled={busy || disabled}>
              <X size={16} /> {rejectLabels[claim.kind]}
            </Button>
          </div>
        ) : (
          <>
            <strong className={cn(
              "research-decision-state",
              `research-decision-state-${decision}`,
              decision === "accepted" && claim.confidence !== "high" && "research-decision-state-provisional"
            )}>{stateLabel}</strong>
            <Button variant="secondary" type="button" onClick={() => onDecision("pending")} disabled={busy || disabled}>
              <Pencil size={16} /> Change decision
            </Button>
          </>
        )}
      </footer>
    </article>
  );
});

function ResearchDecisionReasoning({ basis, rationale }: { basis?: string; rationale: DecisionClaim["rationale"] }) {
  const reasoningID = useId();
  const summaryRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);
  const summary = basis || rationale;
  const additionalReasoning = rationale && rationale !== summary ? rationale : undefined;

  useEffect(() => {
    if (expanded) return;
    const element = summaryRef.current;
    if (!element) return;

    const measure = () => setOverflowing(element.scrollHeight > element.clientHeight + 1);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(measure);
      observer.observe(element);
      return () => observer.disconnect();
    }
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [expanded, summary]);

  if (!summary) return null;

  return (
    <>
      <div id={reasoningID} className="research-decision-reasoning" data-expanded={expanded}>
        <p ref={summaryRef}>{summary}</p>
        {expanded && additionalReasoning ? <p>{additionalReasoning}</p> : null}
      </div>
      {overflowing || additionalReasoning || expanded ? (
        <Button
          className="research-decision-reasoning-toggle"
          variant="ghost"
          size="compact"
          type="button"
          aria-controls={reasoningID}
          aria-expanded={expanded}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? "Show less reasoning" : "Show full reasoning"}
        </Button>
      ) : null}
    </>
  );
}
